import React, { Fragment, useRef, useEffect, useState } from "react";
import produce from "immer";
import { useTranslation } from "react-i18next";
import { disableScroll, enableScroll } from "@assets/js/scrolllock";
import PropTypes from "prop-types";
import { IMAGE } from "@constants";
import PDPSwatchTile from "../PDPSwatchTile";
import PDPUniqueMaterialTile from "../PDPUniqueMaterialTile";
import { log } from "@assets/js/utils";
import ViewportSizes from "@assets/js/viewport-sizes";

import {
  useAvailableMaterialIds,
  useAllowedMaterialIds,
  useActiveMaterialIndex,
  useActiveUniqueMaterial,
} from "@store/pdp";

import "./pdp-material-selector.scss";

const PDPMaterialSelector = ({
  materialSet,
  materials,
  itemClickHandler,
  currentViewport,
  hasHiddenConfigurationElement,
}) => {
  const { t } = useTranslation();

  const texts = {
    materials: t("FritzHansen_Project_ConsumersAndProfessionals_PDP_Materials"),
    uniqueMaterials: t("FritzHansen_Project_ConsumersAndProfessionals_PDP_UniqueMaterials"),
    selectMaterial: t(
      "FritzHansen_Project_ConsumersAndProfessionals_PDP_SelectMaterial"
    ),
    selectedMaterial: t(
      "FritzHansen_Project_ConsumersAndProfessionals_PDP_SelectedMaterial"
    ),
    selectUniqueMaterial: t(
      "FritzHansen_Project_ConsumersAndProfessionals_PDP_SelectUniqueMaterial"
    ),
    moreInfo: t("FritzHansen_Project_ConsumersAndProfessionals_PDP_MoreInfo"),
    uniqueMaterialDefaultSelection: t("FritzHansen_Project_ConsumersAndProfessionals_PDP_UniqueMaterialDefaultSelection")
  };
  const rootRef = useRef();
  const [availableMaterialIds] = useAvailableMaterialIds();

  const [allowedMaterialIds] = useAllowedMaterialIds();

  const [activeMaterialIndex] = useActiveMaterialIndex();

  const [activeUniqueMaterial, setActiveUniqueMaterial] = useActiveUniqueMaterial();

  const isAvailableMaterial = (item) => {
    const foundIndex = availableMaterialIds[activeMaterialIndex].indexOf(
      item.materialId
    );
    const isAvailable = foundIndex > -1;
    // if (!isAvailable) console.log(`materialId "${item.materialId}" is not available`);
    return isAvailable;
  };

  const isAllowedMaterial = (item) => {
    const foundIndex = allowedMaterialIds[activeMaterialIndex].indexOf(
      item.materialId
    );
    const isAllowed = foundIndex > -1;
    // if (!isAllowed) console.log(`materialId "${item.materialId}" is not allowed`);
    return isAllowed;
  };

  //parse used material types
  let filters = [];
  materials.forEach((item) => {
    filters.push({
      material: item,
      isActive: false,
    });
  });

  const [filterList, setFilterList] = useState(filters);
  const [activeFilter, setActiveFilter] = useState(null);

  const uniqueMaterialFilterClickHandler = (item) => {
    const newFilters = produce(filterList, (tmpList) => {
      tmpList.forEach((filter) => {
        if (item.materialId === filter.material.materialId) filter.isActive = !filter.isActive;
        else if (item.materialId !== filter.material.materialId) filter.isActive = false;
      });
    });
    setFilterList(newFilters);
    if (activeFilter && activeFilter.materialId === item.materialId) {
      setActiveFilter(null);
    } else {
      setActiveFilter(item);
    }
  };

  const clickHandler = (item, isUnallowed, uniqueMaterial) => {
    // displayname disabled
    // setSelectedMaterialItem(item);

    itemClickHandler(item, isUnallowed, uniqueMaterial);
  };

  useEffect(() => {
    if (
      currentViewport.name === ViewportSizes.SMALL.name ||
      currentViewport.name === ViewportSizes.MEDIUM.name
    ) {
      disableScroll();
    }
    return () => {
      enableScroll();
    };
  }, [currentViewport.name]);

  const hasUniqueMaterials = !!materialSet.uniqueMaterials;

  const isActiveMaterial = (materialId) => {
    return activeFilter && materialId === activeFilter.materialId;
  };

  if (!hasUniqueMaterials) {
    return (
      <div className="pdp-material-selector">
        <h3>{texts.selectMaterial}</h3>
        <div className="pdp-material-selector__info">
          {materialSet.isPrimary && (
            <h5>
              {materialSet.label} – {materials.length} {texts.materials}
            </h5>
          )}
          {materialSet.urlSlug && (
            <a
              className="basic-link"
              href={materialSet.urlSlug}
              target="_blank"
              rel="noreferrer"
            >
              <span className="text">{texts.moreInfo}</span>
            </a>
          )}
        </div>

        <div className="pdp-material-selector__content" ref={rootRef}>
          {materials && (
            <div className="pdp-material-selector__tiles">
              {materials.map((item) => {
                return (
                  <Fragment key={`materialTile${item.materialId}`}>
                    {isAvailableMaterial(item) && (
                      <PDPSwatchTile
                        {...item}
                        title={item.materialLabel.split("(")[0]}
                        tag={item.materialLabel.split("(")[1]?.split(")")[0]}
                        item={item}
                        imageType={IMAGE.SWATCH}
                        isUnallowed={!isAllowedMaterial(item)}
                        itemClickHandler={clickHandler}
                        hasHiddenConfigurationElement={hasHiddenConfigurationElement}
                      />
                    )}
                  </Fragment>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Unique materials
  let uniqueMaterialsCount = 0;
  Object.keys(materialSet?.uniqueMaterials).forEach(key => { uniqueMaterialsCount += materialSet.uniqueMaterials[key].length });

  return (   
       <div className="pdp-material-selector">
        <h3>{texts.selectMaterial}</h3>
        <div className="pdp-material-selector__content" ref={rootRef}>
          <div className="pdp-material-selector__tiles">
            {filterList.map((item) => {
              return (
                <Fragment key={`materialFilterTile${item.material.materialId}`}>
                  <PDPSwatchTile
                    imgUrl={""}
                    {...item.material}
                    isActive={item.isActive}
                    title={item.material.materialLabel.split("(")[0]}
                    item={item.material}
                    imageType={IMAGE.SWATCH}
                    isUnallowed={false}
                    itemClickHandler={uniqueMaterialFilterClickHandler}
                  />
                </Fragment>
              );
            })}
          </div>
          {activeFilter && (
            <>
            <h3>{texts.selectUniqueMaterial}</h3>
            <div className="pdp-material-selector__tiles">
              {materials.map((item) => {
                const matchingUniqueMaterialSelection = materialSet.uniqueMaterials[item.materialId]
                if (matchingUniqueMaterialSelection && isActiveMaterial(item.materialId)) {
                  log("matchingUniqueMaterialSelection", matchingUniqueMaterialSelection)
                  return (
                    <>
                      <Fragment key={`materialTile${item.materialId}`}>
                        {isAvailableMaterial(item) && (
                          <PDPSwatchTile
                            {...item}
                            isActive={!activeUniqueMaterial}
                            title={item.materialLabel.split("(")[0]}
                            tag={texts.uniqueMaterialDefaultSelection}
                            item={item}
                            imageType={IMAGE.SWATCH}
                            isUnallowed={!isAllowedMaterial(item)}
                            itemClickHandler={clickHandler}
                          />
                        )}
                      </Fragment>
                      {matchingUniqueMaterialSelection.map(uniqueMaterial => {
                        return (
                          <Fragment key={`materialTile${item.materialId}${uniqueMaterial.id}`}>
                            <PDPUniqueMaterialTile
                              uniqueMaterial={uniqueMaterial}
                              imgUrl={uniqueMaterial.imageUrl}
                              {...item}
                              isActive={activeUniqueMaterial && uniqueMaterial.id === activeUniqueMaterial.id}
                              title={item.materialLabel.split("(")[0] + " " + uniqueMaterial.id}
                              tag={item.materialLabel.split("(")[1]?.split(")")[0]}
                              item={item}
                              imageType={IMAGE.SWATCH}
                              isUnallowed={!isAllowedMaterial(item)}
                              itemClickHandler={clickHandler}
                            />
                          </Fragment>
                        );
                      })}
                    </>
                  )
                }
              })}
            </div>
            </>
          )}
        </div>
      </div>
  );
};

PDPMaterialSelector.propTypes = {
  materials: PropTypes.array.isRequired,
  materialSet: PropTypes.object.isRequired,
  filters: PropTypes.object,
};

export default PDPMaterialSelector;
